<template>
  <div class="support-file-preview-list">
    <ul
      class="support-file-previews"
      :class="{
        'support-file-previews--compact': compact,
        'support-file-previews--message': message,
        'support-file-previews--queued': queued
      }"
    >
      <li
        v-for="(file, index) in files"
        :key="file.id || `${fileName(file)}-${index}`"
        class="support-file-preview"
        :class="{
          'support-file-preview--image': isImage(file) && fileUrl(file, index),
          'support-file-preview--document': !isImage(file)
        }"
      >
        <button
          v-if="isImage(file) && fileUrl(file, index)"
          type="button"
          class="support-file-preview__open"
          :title="`Preview ${fileName(file)}`"
          :aria-label="`Preview ${fileName(file)}`"
          @click="openImage(index)"
        >
          <img :src="fileUrl(file, index)" :alt="fileName(file)">
        </button>
        <button
          v-else-if="file.download_url"
          type="button"
          class="support-file-preview__open"
          :title="`Open ${fileName(file)}`"
          @click="openFile(file, index)"
        >
          <span class="support-file-preview__type" aria-hidden="true">{{ extension(file) }}</span>
        </button>
        <div v-else class="support-file-preview__open">
          <span class="support-file-preview__type" aria-hidden="true">{{ extension(file) }}</span>
        </div>
        <div v-if="!queued && (!message || !isImage(file))" class="support-file-preview__details">
          <strong :title="fileName(file)">{{ fileName(file) }}</strong>
          <small>{{ formatSize(file.size) }}<template v-if="!file.download_url"> · Ready to send</template></small>
        </div>
        <button
          v-if="removable && (!file.download_url || file.can_delete)"
          type="button"
          class="support-file-preview__remove"
          :aria-label="`Remove ${fileName(file)}`"
          @click="$emit('remove', { file, index })"
        >×</button>
      </li>
    </ul>

    <support-image-gallery-modal
      :name="previewModalName"
      :images="imageEntries"
      :initial-index="activeImageIndex"
    />
  </div>
</template>

<script>
import { getSupportTicketAttachment } from '@/http'
import SupportImageGalleryModal from './SupportImageGalleryModal'

let previewInstance = 0

export default {
  name: 'SupportFilePreviewList',
  components: { SupportImageGalleryModal },
  props: {
    files: { type: Array, default: () => [] },
    removable: { type: Boolean, default: true },
    compact: Boolean,
    message: Boolean,
    queued: Boolean
  },
  data() {
    previewInstance += 1
    return {
      activeImageIndex: 0,
      previewModalName: `support-image-preview-${previewInstance}`,
      previewUrls: [],
      previewRequest: 0
    }
  },
  computed: {
    imageEntries() {
      return this.files.map((file, index) => ({
        file,
        key: file.id || `${this.fileName(file)}-${index}`,
        name: this.fileName(file),
        sourceIndex: index,
        url: this.fileUrl(file, index)
      })).filter(item => this.isImage(item.file) && item.url)
    }
  },
  watch: {
    files: {
      immediate: true,
      deep: true,
      handler() { this.refreshPreviews() }
    }
  },
  beforeDestroy() { this.previewRequest += 1; this.revokePreviews() },
  methods: {
    fileName(file) { return file.original_name || file.name || 'Attachment' },
    mimeType(file) { return file.mime_type || file.type || '' },
    isImage(file) {
      return this.mimeType(file).startsWith('image/') || ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(this.extension(file))
    },
    fileUrl(file, index) { return this.previewUrls[index] || '' },
    extension(file) {
      const parts = this.fileName(file).split('.')
      return parts.length > 1 ? parts.pop().slice(0, 5).toUpperCase() : 'FILE'
    },
    formatSize(size) {
      if (!size && size !== 0) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    openImage(sourceIndex) {
      const imageIndex = this.imageEntries.findIndex(item => item.sourceIndex === sourceIndex)
      if (imageIndex < 0) return
      this.activeImageIndex = imageIndex
      this.$nextTick(() => this.$modal.show(this.previewModalName))
    },
    async openFile(file, index) {
      if (this.isImage(file) && this.fileUrl(file, index)) {
        this.openImage(index)
        return
      }
      try {
        const response = await getSupportTicketAttachment(file.download_url)
        const url = URL.createObjectURL(response.data)
        if (this.isImage(file)) {
          this.$set(this.previewUrls, index, url)
          this.$nextTick(() => this.openImage(index))
          return
        }
        const link = document.createElement('a')
        link.href = url
        link.download = this.fileName(file)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 0)
      } catch (error) {
        this.$notify({ type: 'error', text: 'The attachment could not be loaded.' })
      }
    },
    revokePreviews() {
      this.previewUrls.forEach(url => { if (url) URL.revokeObjectURL(url) })
    },
    async refreshPreviews() {
      const request = ++this.previewRequest
      this.revokePreviews()
      this.previewUrls = this.files.map(file => {
        if (!file.download_url && this.isImage(file) && typeof URL !== 'undefined') return URL.createObjectURL(file)
        return ''
      })
      await Promise.all(this.files.map(async (file, index) => {
        if (!file.download_url || !this.isImage(file)) return
        try {
          const response = await getSupportTicketAttachment(file.download_url)
          if (request !== this.previewRequest) return
          this.$set(this.previewUrls, index, URL.createObjectURL(response.data))
        } catch (error) {
          // Keep the file control available so the user can retry explicitly.
        }
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.support-file-preview-list { min-width: 0; }
.support-file-previews { display: flex; flex-wrap: wrap; gap: 10px; margin: 10px 0 0; padding: 0; list-style: none; }
.support-file-preview { position: relative; display: grid; width: 210px; min-width: 0; align-items: center; grid-template-columns: 52px minmax(0, 1fr); gap: 10px; padding: 8px 32px 8px 8px; border: 1px solid $ui-line; border-radius: 11px; background: $ui-surface; box-shadow: 0 3px 10px rgba(28, 45, 74, .04); }
.support-file-preview__open { display: grid; width: 52px; height: 52px; place-items: center; overflow: hidden; padding: 0; border: 0; border-radius: 8px; background: $ui-blue-soft; color: $ui-blue; cursor: pointer; text-decoration: none; }
.support-file-preview__open img { width: 100%; height: 100%; object-fit: cover; }
.support-file-preview__type { font-size: 11px; font-weight: 800; letter-spacing: .04em; }
.support-file-preview__details { display: grid; min-width: 0; gap: 3px; }
.support-file-preview__details strong { overflow: hidden; color: $ui-ink; font-size: 13px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.support-file-preview__details small { color: $ui-muted; font-size: 11px; }
.support-file-preview__remove { position: absolute; top: 6px; right: 7px; display: grid; width: 22px; height: 22px; place-items: center; padding: 0; border: 0; border-radius: 50%; background: transparent; color: $ui-muted; cursor: pointer; font-size: 18px; line-height: 1; }
.support-file-preview__remove:hover { background: #fff1f2; color: $ui-red; }
.support-file-previews--compact { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); }
.support-file-previews--compact .support-file-preview { width: auto; }
.support-file-previews--queued { gap: 8px; margin: 8px 0 2px; }
.support-file-previews--queued .support-file-preview { display: block; width: 76px; height: 76px; flex: 0 0 76px; overflow: hidden; padding: 0; border-radius: 10px; }
.support-file-previews--queued .support-file-preview__open { width: 100%; height: 100%; border-radius: 0; }
.support-file-previews--queued .support-file-preview__type { font-size: 12px; }
.support-file-previews--queued .support-file-preview__remove { top: 5px; right: 5px; width: 22px; height: 22px; background: rgba(9, 18, 31, .76); color: #fff; font-size: 17px; }
.support-file-previews--queued .support-file-preview__remove:hover { background: $ui-red; color: #fff; }
.support-file-previews--message { display: grid; width: 100%; max-width: 560px; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px; margin-top: 10px; }
.support-file-previews--message .support-file-preview { width: auto; }
.support-file-previews--message .support-file-preview--image { display: block; height: 150px; overflow: hidden; padding: 0; border: 0; border-radius: 10px; background: $ui-surface-soft; box-shadow: none; }
.support-file-previews--message .support-file-preview--image:first-child:nth-last-child(odd) { height: 260px; grid-column: 1 / -1; }
.support-file-previews--message .support-file-preview--image:only-child { height: 340px; max-height: 48vh; grid-column: 1 / -1; }
.support-file-previews--message .support-file-preview--image .support-file-preview__open { width: 100%; height: 100%; border-radius: 0; }
.support-file-previews--message .support-file-preview--document { grid-column: 1 / -1; }
.support-file-previews--message .support-file-preview__remove { background: rgba(9, 18, 31, .72); color: #fff; }
.support-file-previews--message .support-file-preview__remove:hover { background: $ui-red; color: #fff; }
@media (max-width: 680px) {
  .support-file-previews--message .support-file-preview--image { height: 120px; }
  .support-file-previews--message .support-file-preview--image:first-child:nth-last-child(odd) { height: 210px; }
}
</style>
